/**
 * DeliverySLAList — the reusable entry point for the Delivery SLA UI.
 *
 * Renders Single SLA (one row) or Multi SLA (Best + Fastest) from the mapped
 * view model, and handles loading / error / empty / not-serviceable states.
 * Prop-driven and free of any page-specific logic so it can be dropped into
 * both the PDP "Delivery & Return Details" card and the Cart page.
 *
 * Delivery note from multiSellerSlaConfig:
 *  - 1 card  → oneSlaDeliveryNote (no BEST/FASTEST badge)
 *  - 2+ cards → twoSlaDeliveryNote (badges shown)
 * Title rows use the note as tooltip; lists without a title use it as footnote.
 */
import React, { FC } from 'react';
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import Text from '../../atoms/Text';
import properties from '../../../modules/ajio-util/properties';
import SLARow from './SLARow';
import useDeliverySLA from './useDeliverySLA';
import { SLA_CONTEXT, pickSlaDeliveryNote } from './constants';
import { DeliverySlaListProps } from './types';
import { getMultiSellerSlaConfig } from '../../selectors/featureFlagsSelector';
import styles from './index.pcss';

const cx = classNames.bind(styles);
const infoTooltipIcon = `${properties.getProperty('imageBaseUrl')}/static/img/info-tooltip.svg`;

const SLASkeleton: FC<{ rows: number }> = ({ rows }) => (
  <div className={cx('sla-skeleton')} aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className={cx('sla-skeleton__row')} />
    ))}
  </div>
);

const DeliverySLAList: FC<DeliverySlaListProps> = ({
  slaOptions,
  viewModel: externalViewModel,
  isLoading = false,
  error = false,
  context = SLA_CONTEXT.PDP,
  priceTypeTags,
  slaCodeLabels,
  onSelectOption,
  title,
  footnote,
  titleTooltip,
  luxe = false,
  fleek = false,
  onDialogSurface = false
}) => {
  const { status, viewModel, isMultiSla, selectedId, selectOption } = useDeliverySLA({
    slaOptions,
    viewModel: externalViewModel,
    isLoading,
    error,
    context,
    priceTypeTags,
    slaCodeLabels,
    onSelectOption
  });
  const listClass = cx('sla-list', {
    'sla-list--luxe': luxe,
    'sla-list--fleek': fleek,
    'sla-list--fleek-dialog': fleek && onDialogSurface
  });
  const slaConfig = useSelector((state: any) => getMultiSellerSlaConfig(state));
  const serviceableCount = (viewModel?.options || []).filter(option => option.serviceable)
    .length;
  const cardCount = serviceableCount || (viewModel?.options || []).length;
  const deliveryNote = pickSlaDeliveryNote(slaConfig, cardCount);
  // Explicit non-empty props still win (tests / overrides); otherwise use config.
  const resolvedTitleTooltip = title
    ? typeof titleTooltip === 'string' && titleTooltip.trim()
      ? titleTooltip
      : deliveryNote
    : undefined;
  const resolvedFootnote = !title
    ? typeof footnote === 'string' && footnote.trim()
      ? footnote
      : deliveryNote
    : footnote;

  const renderHeader = (): JSX.Element | null =>
    title ? (
      <div className={cx('sla-list__title')}>
        <Text
          variant="T4"
          weight="bold"
          color={fleek ? 'White' : luxe ? 'Light_Black' : 'Nero'}
          role="heading"
          ariaLevel={3}
        >
          {title}
        </Text>
        {resolvedTitleTooltip && (
          <span
            className={cx('sla-tooltip')}
            tabIndex={0}
            role="button"
            aria-label={resolvedTitleTooltip}
          >
            <img className={cx('sla-tooltip__icon')} src={infoTooltipIcon} alt="" aria-hidden="true" />
            <span className={cx('sla-tooltip__arrow')} aria-hidden="true" />
          </span>
        )}
        {resolvedTitleTooltip && (
          <span className={cx('sla-tooltip__bubble')} role="tooltip">
            {resolvedTitleTooltip}
          </span>
        )}
      </div>
    ) : null;

  if (status === 'loading') {
    return (
      <div className={listClass}>
        {renderHeader()}
        <SLASkeleton rows={1} />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={listClass}>
        {renderHeader()}
        <div className={cx('sla-list__message')} role="alert">
          <Text variant="T2" color="Salmon">
            Unable to fetch delivery details. Please try again.
          </Text>
        </div>
      </div>
    );
  }

  if (status === 'empty') {
    return null;
  }

  if (status === 'not-serviceable') {
    return (
      <div className={listClass}>
        {renderHeader()}
        <div className={cx('sla-list__message')} role="alert">
          <Text variant="T2" weight="bold" color="Salmon">
            {viewModel.reasonForNotServiceability === 'NS'
              ? `Currently we do not deliver to ${viewModel.pinCode}`
              : 'This product is not deliverable to your location or out of stock'}
          </Text>
        </div>
      </div>
    );
  }

  const serviceableOptions = viewModel.options.filter(option => option.serviceable);
  const showBadges = isMultiSla && serviceableOptions.length > 1;

  return (
    <div className={listClass}>
      {renderHeader()}
      <div
        className={cx('sla-list__options')}
        role="radiogroup"
        aria-label={title || 'Delivery options'}
      >
        {viewModel.options.map(option => (
          <SLARow
            key={option.id}
            option={option}
            selected={option.id === selectedId}
            showBadge={showBadges}
            onSelect={selectOption}
            fleek={fleek}
            luxe={luxe}
            perPiece={context === SLA_CONTEXT.CART}
          />
        ))}
      </div>
      {resolvedFootnote && (
        <div className={cx('sla-list__footnote')}>
          <Text variant="T2" color={fleek ? 'White' : 'Dark_Grey'}>
            {resolvedFootnote}
          </Text>
        </div>
      )}
    </div>
  );
};

export default DeliverySLAList;
